import { useEffect, useState } from 'react'
import nexusApi from '../../services/nexusApi'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts'
import {
  Building2, Users, DollarSign, AlertTriangle, CheckCircle,
  XCircle, TrendingUp, Clock, Activity, ChevronRight
} from 'lucide-react'
import { Link } from 'react-router-dom'

const NX = {
  bg: '#0D1117', surface: '#161B22', surface2: '#1C2330', border: '#2A3441',
  text: '#E6EDF3', textDim: '#8B949E', accent: '#0066FF',
  green: '#3DB54A', amber: '#F5921E', red: '#EF4444',
}

function MetricCard({ label, value, sub, color, icon: Icon }: any) {
  return (
    <div style={{
      background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12,
      padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ color: NX.textDim, fontSize: '0.8125rem', fontWeight: 500 }}>{label}</span>
        <div style={{
          width: 36, height: 36, borderRadius: 8, background: `${color}20`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={18} color={color} />
        </div>
      </div>
      <div style={{ color: NX.text, fontSize: '1.875rem', fontWeight: 700, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ color: NX.textDim, fontSize: '0.75rem' }}>{sub}</div>}
    </div>
  )
}

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
      background: ok ? NX.green : NX.red, marginRight: 6,
    }} />
  )
}

export default function NexusDashboard() {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    nexusApi.get('/dashboard').then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false))
  }, [])

  if (loading) return (
    <div style={{ padding: '2rem', color: NX.textDim, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <Activity size={16} />  Loading dashboard...
    </div>
  )

  if (!data) return <div style={{ padding: '2rem', color: NX.red }}>Failed to load dashboard</div>

  const { metrics, alerts, revenueByPlan, recentSignups, mrrTrend, health } = data

  return (
    <div style={{ padding: '2rem', background: NX.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.75rem' }}>
        <div>
          <h1 style={{ color: NX.text, fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>Platform Dashboard</h1>
          <p style={{ color: NX.textDim, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            {new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>
        {/* Health status bar */}
        <div style={{
          background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 8,
          padding: '0.5rem 1rem', display: 'flex', gap: '1.5rem',
        }}>
          <span style={{ fontSize: '0.8125rem', color: NX.textDim }}>
            <StatusDot ok={health.api === 'ok'} />API
          </span>
          <span style={{ fontSize: '0.8125rem', color: NX.textDim }}>
            <StatusDot ok={health.db === 'ok'} />Database
          </span>
        </div>
      </div>

      {/* Hero metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        <MetricCard label="Total Tenants" value={metrics.totalTenants} sub={`${metrics.activeTenants} active · ${metrics.trialTenants} trials`} color={NX.accent} icon={Building2} />
        <MetricCard label="MRR" value={`$${metrics.mrr.toLocaleString()}`} sub={`ARR $${metrics.arr.toLocaleString()}`} color={NX.green} icon={DollarSign} />
        <MetricCard label="Active Users" value={metrics.totalUsers.toLocaleString()} sub={`${metrics.activeUsersToday} active today`} color={NX.amber} icon={Users} />
        <MetricCard label="Open Tickets" value={metrics.openTickets} sub={metrics.p1Tickets > 0 ? `${metrics.p1Tickets} P1 critical` : 'No P1 tickets'} color={metrics.p1Tickets > 0 ? NX.red : NX.textDim} icon={AlertTriangle} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* MRR Chart */}
        <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, padding: '1.25rem' }}>
          <h3 style={{ color: NX.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 1.25rem' }}>
            MRR Trend — Last 12 Months
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={mrrTrend} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <defs>
                <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={NX.accent} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={NX.accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={NX.border} />
              <XAxis dataKey="month" tick={{ fill: NX.textDim, fontSize: 11 }} />
              <YAxis tick={{ fill: NX.textDim, fontSize: 11 }} tickFormatter={v => `$${v}`} />
              <Tooltip
                contentStyle={{ background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text }}
                formatter={(v: any) => [`$${v.toLocaleString()}`, 'MRR']}
              />
              <Area type="monotone" dataKey="mrr" stroke={NX.accent} strokeWidth={2} fill="url(#mrrGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Alerts */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Urgent alerts */}
          <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, padding: '1.25rem', flex: 1 }}>
            <h3 style={{ color: NX.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AlertTriangle size={16} color={NX.amber} /> Urgent Alerts
            </h3>
            {metrics.p1Tickets > 0 && (
              <div style={{ background: 'rgba(239,68,68,0.1)', border: `1px solid ${NX.red}`, borderRadius: 8, padding: '0.625rem 0.875rem', marginBottom: '0.625rem' }}>
                <span style={{ color: NX.red, fontSize: '0.8125rem', fontWeight: 600 }}>{metrics.p1Tickets} P1 Ticket{metrics.p1Tickets > 1 ? 's' : ''}</span>
                <span style={{ color: NX.textDim, fontSize: '0.8125rem' }}> require immediate attention</span>
              </div>
            )}
            {alerts.expiringSoon.length > 0 && (
              <div style={{ background: 'rgba(245,146,30,0.1)', border: `1px solid ${NX.amber}`, borderRadius: 8, padding: '0.625rem 0.875rem', marginBottom: '0.625rem' }}>
                <span style={{ color: NX.amber, fontSize: '0.8125rem', fontWeight: 600 }}>{alerts.expiringSoon.length} trial{alerts.expiringSoon.length > 1 ? 's' : ''}</span>
                <span style={{ color: NX.textDim, fontSize: '0.8125rem' }}> expiring in 7 days</span>
              </div>
            )}
            {metrics.p1Tickets === 0 && alerts.expiringSoon.length === 0 && (
              <div style={{ color: NX.textDim, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <CheckCircle size={14} color={NX.green} /> No urgent alerts
              </div>
            )}
          </div>

          {/* Today's stats */}
          <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, padding: '1.25rem' }}>
            <h3 style={{ color: NX.text, fontSize: '0.9375rem', fontWeight: 600, margin: '0 0 0.875rem' }}>Today</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: NX.textDim, fontSize: '0.8125rem' }}>New signups</span>
                <span style={{ color: NX.text, fontSize: '0.8125rem', fontWeight: 600 }}>{metrics.newToday}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: NX.textDim, fontSize: '0.8125rem' }}>Active users</span>
                <span style={{ color: NX.text, fontSize: '0.8125rem', fontWeight: 600 }}>{metrics.activeUsersToday}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: NX.textDim, fontSize: '0.8125rem' }}>Incidents logged</span>
                <span style={{ color: NX.text, fontSize: '0.8125rem', fontWeight: 600 }}>{metrics.incidentsToday}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: NX.textDim, fontSize: '0.8125rem' }}>This month signups</span>
                <span style={{ color: NX.text, fontSize: '0.8125rem', fontWeight: 600 }}>{metrics.newThisMonth}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Revenue by plan */}
        <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, padding: '1.25rem' }}>
          <h3 style={{ color: NX.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 1rem' }}>Revenue by Plan</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            {revenueByPlan.map((row: any) => (
              <div key={row.plan} style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                <span style={{
                  display: 'inline-block', width: 80, padding: '0.2rem 0.5rem',
                  borderRadius: 4, background: NX.surface2, color: NX.textDim,
                  fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize',
                }}>
                  {row.plan}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: NX.text, fontSize: '0.8125rem' }}>{row.count} tenants</span>
                    <span style={{ color: NX.green, fontSize: '0.8125rem', fontWeight: 600 }}>${row.mrr.toLocaleString()}/mo</span>
                  </div>
                  <div style={{ height: 4, background: NX.border, borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', background: NX.accent, borderRadius: 2,
                      width: `${Math.min(100, (row.mrr / Math.max(1, revenueByPlan.reduce((s: number, r: any) => s + r.mrr, 0))) * 100)}%`,
                    }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent signups */}
        <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, padding: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h3 style={{ color: NX.text, fontSize: '1rem', fontWeight: 600, margin: 0 }}>Recent Signups</h3>
            <Link to="/nexus/tenants" style={{ color: NX.accent, fontSize: '0.8125rem', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 2 }}>
              View all <ChevronRight size={12} />
            </Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {recentSignups.slice(0, 5).map((t: any) => (
              <Link key={t.id} to={`/nexus/tenants/${t.id}`} style={{ textDecoration: 'none' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.5rem 0.625rem', borderRadius: 8, background: NX.surface2,
                  cursor: 'pointer',
                }}>
                  <div>
                    <div style={{ color: NX.text, fontSize: '0.8125rem', fontWeight: 500 }}>{t.name}</div>
                    <div style={{ color: NX.textDim, fontSize: '0.6875rem' }}>{t.user_count} users · {t.industry || 'N/A'}</div>
                  </div>
                  <span style={{
                    padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.6875rem', fontWeight: 600,
                    background: t.plan === 'trial' ? 'rgba(245,146,30,0.15)' : 'rgba(61,181,74,0.15)',
                    color: t.plan === 'trial' ? NX.amber : NX.green,
                    textTransform: 'capitalize',
                  }}>
                    {t.plan}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
